import { Button, CircularProgress, FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { colours } from "../../../StyleConstants";
import LassoModal from "../../../components/LassoModal";
import { LassoTextField } from "../../../components/LassoTextField";
import { setNewNotification } from "../../common/notificationSlice";
import { useInitiateOwnershipTransferMutation } from "../../../api/ducApiSlice";
import SubRecipientsErrorModal from "./SubRecipientsErrorModal";

const EMAIL_PATTERN = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

interface FormValues {
    newPiFirstName: string
    newPiLastName: string
    newPiEmail: string
    newPiInstitution: string
    reasonCategory: string
    reasonOtherText: string
    initialSoName: string
    initialSoEmail: string
}

interface TransferOwnershipModalProps {
    open: boolean
    setOpen: (open: boolean) => void
    ducDetailsId: number
}

const TransferOwnershipModal = ({ open, setOpen, ducDetailsId }: TransferOwnershipModalProps) => {

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        defaultValues: {
            newPiFirstName: "",
            newPiLastName: "",
            newPiEmail: "",
            newPiInstitution: "",
            reasonCategory: "MOVING_INSTITUTION",
            reasonOtherText: "",
            initialSoName: "",
            initialSoEmail: "",
        },
    });

    const [initiateTransfer] = useInitiateOwnershipTransferMutation();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string[]>([]);
    const [openErrorModal, setOpenErrorModal] = useState(false);

    const reasonCategory = watch("reasonCategory");

    const handleClose = () => {
        setOpen(false);
    };

    const onSubmit = (data: FormValues) => {
        setLoading(true);
        initiateTransfer({ ducDetailsId, ...data }).then((response: any) => {
            setLoading(false);
            const hasErrorArray = response?.error && "data" in response.error && Array.isArray(response.error?.data?.errors);

            if (hasErrorArray) {
                setError(response.error.data.errors as string[]);
                setOpenErrorModal(true);
            } else if (response?.error) {
                dispatch(setNewNotification({
                    message: response?.error?.data || "Unable to initiate Lead Investigator change. Please try again later.",
                    type: 'error'
                }));
            } else {
                dispatch(setNewNotification({
                    message: "Lead Investigator change initiated. " + data.newPiFirstName + " " + data.newPiLastName + " has been notified.",
                    type: 'success'
                }));
                reset();
                setOpen(false);
            }
        });
    };

    return (
        <>
            <LassoModal open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                {!loading &&
                    <Stack gap={4} padding="10px">
                        <Stack flexDirection="row" justifyContent="space-between">
                            <Typography variant="h5" fontWeight="600" color={colours.midnightBlue.blue}>
                                Change Lead Investigator
                            </Typography>
                            <HighlightOffIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
                        </Stack>
                        <Typography color={colours.midnightBlue.blue}>
                            Fill out the information for the new owner of this DUC group
                        </Typography>
                        <Stack flexDirection="row" gap={2}>
                            <Controller
                                name="newPiFirstName"
                                control={control}
                                rules={{ required: "This field is required", pattern: /^(?!\s*$).+/ }}
                                render={({ field }) =>
                                    <LassoTextField
                                        label="New investigator first name"
                                        fullWidth
                                        required
                                        error={'newPiFirstName' in errors}
                                        helperText={errors?.newPiFirstName?.message || ""}
                                        {...field}
                                    />}
                            />
                            <Controller
                                name="newPiLastName"
                                control={control}
                                rules={{ required: "This field is required", pattern: /^(?!\s*$).+/ }}
                                render={({ field }) =>
                                    <LassoTextField
                                        label="New investigator last name"
                                        fullWidth
                                        required
                                        error={'newPiLastName' in errors}
                                        helperText={errors?.newPiLastName?.message || ""}
                                        {...field}
                                    />}
                            />
                        </Stack>
                        <Controller
                            name="newPiEmail"
                            control={control}
                            rules={{
                                required: "This field is required",
                                pattern: { value: EMAIL_PATTERN, message: 'Invalid email address' }
                            }}
                            render={({ field }) =>
                                <LassoTextField
                                    label="New investigator email address"
                                    fullWidth
                                    required
                                    error={'newPiEmail' in errors}
                                    helperText={errors?.newPiEmail?.message || ""}
                                    {...field}
                                />}
                        />
                        <Stack gap={0.5}>
                            <Controller
                                name="newPiInstitution"
                                control={control}
                                rules={{ required: "This field is required", pattern: /^(?!\s*$).+/ }}
                                render={({ field }) =>
                                    <LassoTextField
                                        label="New investigator institutional affiliation"
                                        fullWidth
                                        required
                                        error={'newPiInstitution' in errors}
                                        helperText={errors?.newPiInstitution?.message || ""}
                                        {...field}
                                    />}
                            />
                            <Typography variant="caption" color={colours.gray.gray70}>
                                Must match your current institution - a DUC cannot transfer to another institution.
                            </Typography>
                        </Stack>
                        <Stack flexDirection="row" gap={2}>
                            <Controller
                                name="initialSoName"
                                control={control}
                                render={({ field }) =>
                                    <LassoTextField
                                        label="Signing Official name (optional)"
                                        fullWidth
                                        {...field}
                                    />}
                            />
                            <Controller
                                name="initialSoEmail"
                                control={control}
                                rules={{
                                    validate: (value) => !value || EMAIL_PATTERN.test(value) || 'Invalid email address'
                                }}
                                render={({ field }) =>
                                    <LassoTextField
                                        label="Signing Official email (optional)"
                                        fullWidth
                                        error={'initialSoEmail' in errors}
                                        helperText={errors?.initialSoEmail?.message || ""}
                                        {...field}
                                    />}
                            />
                        </Stack>
                        <Stack gap={1}>
                            <Typography color={colours.midnightBlue.blue} fontWeight="600">
                                Reason for the change
                            </Typography>
                            <Controller
                                name="reasonCategory"
                                control={control}
                                render={({ field }) =>
                                    <RadioGroup {...field}>
                                        <FormControlLabel value="MOVING_INSTITUTION" control={<Radio />} label="Moving to another institution" />
                                        <FormControlLabel value="MOVING_TO_SUBAPPLICANT" control={<Radio />} label="Moving to a sub-applicant role" />
                                        <FormControlLabel value="OTHER" control={<Radio />} label="Other" />
                                    </RadioGroup>}
                            />
                            {reasonCategory === "OTHER" &&
                                <Controller
                                    name="reasonOtherText"
                                    control={control}
                                    rules={{ required: "Please describe the reason for this change" }}
                                    render={({ field }) =>
                                        <LassoTextField
                                            label="Reason"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            error={'reasonOtherText' in errors}
                                            helperText={errors?.reasonOtherText?.message || ""}
                                            {...field}
                                        />}
                                />
                            }
                        </Stack>
                        <Button
                            variant="primary"
                            onClick={handleSubmit(onSubmit)}
                            sx={{ alignSelf: "center" }}
                        >
                            Confirm Lead Investigator Change
                        </Button>
                    </Stack>}

                {loading &&
                    <Stack alignItems="center">
                        <CircularProgress />
                    </Stack>}
            </LassoModal>
            <SubRecipientsErrorModal error={error} open={openErrorModal} setOpen={setOpenErrorModal} />
        </>
    );
};

export default TransferOwnershipModal;
